import config from 'config';
import { authHeader } from '../_helpers';

export const userService = {
    login,
    logout,
    getAccountDetails,
    getAddresses,
    getAddress,
    getJob,
    getJobs,
    getInvoice,
    getInvoices,
    getItemTypes,
    getLocations,
    getMotd,
    getServices,
    getUsers,
    getUser,
    getVehicleTypes,
    recoverPassword,
    submitJob,
    submitUser,
    deleteUser,
    changePassword,
    getUserPreferences,
    submitUserPreferences
};

function login(key, secret) {
    const requestOptions = {
        crossDomain: true,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, secret })
    };

    return fetch(`${config.apiUrl}/auth/login`, requestOptions)
        .then(handleResponse)
        .then(token => {
            // login successful if there's a user in the response
            if (token && token.token_type == 'bearer') {
                // store user details and basic auth credentials in local storage
                // to keep user logged in between page refreshes
                localStorage.setItem('token', JSON.stringify(token));
            }
    
            return token;
        });
}

function logout() {
    // remove user from local storage to log user out
    localStorage.removeItem('token');
}


function getMotd() {
    const requestOptions = {
        method: 'GET',
        headers: authHeader()
    };

    return fetch(`${config.apiUrl}/references/messages`, requestOptions).then(handleResponse);
}

function getAccountDetails(account) {
    const requestOptions = {
        method: 'GET',
        headers: authHeader()
    };

    var url = new URL(`${config.apiUrl}/users/me/accounts`);
    var params = { account: account };
    Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));
    return fetch(url, requestOptions).then(handleResponse);
}

function getUser(user_id) {
    const requestOptions = {
        method: 'GET',
        headers: authHeader()
    };

    if (user_id === null || user_id == undefined)
    {
        return fetch(`${config.apiUrl}/users/me`, requestOptions).then(handleResponse);
    }
    else
    {
        return fetch(`${config.apiUrl}/users/` + user_id, requestOptions).then(handleResponse);
    }
}

function getUsers() {
    const requestOptions = {
        method: 'GET',
        headers: authHeader()
    };

    return fetch(`${config.apiUrl}/users`, requestOptions).then(handleResponse);
}

function getLocations() {
    const requestOptions = {
        method: 'GET',
        headers: authHeader()
    };

    return fetch(`${config.apiUrl}/locations`, requestOptions).then(handleResponse);
}

function getItemTypes() {
    const requestOptions = {
        method: 'GET',
        headers: authHeader()
    };

    return fetch(`${config.apiUrl}/references/itemtypes`, requestOptions).then(handleResponse);
}

function getVehicleTypes() {
    const requestOptions = {
        method: 'GET',
        headers: authHeader()
    };

    return fetch(`${config.apiUrl}/references/vehicletypes`, requestOptions).then(handleResponse);
}

function getServices(jobdata) {
    const requestOptions = {
        method: 'GET',
        headers: authHeader()
    };

    var url = new URL(`${config.apiUrl}/jobs/services`);
    Object.keys(jobdata).forEach(key => url.searchParams.append(key, jobdata[key]));
    return fetch(url, requestOptions).then(handleResponse);
}

function submitJob(jobdata) {
    var headers = authHeader();
    headers['Content-Type'] = 'application/json';
    const requestOptions = {
        crossDomain: true,
        method: 'POST',
        headers: headers,
        body: JSON.stringify(jobdata)
    };

    var url = new URL(`${config.apiUrl}/jobs`);
    return fetch(url, requestOptions).then(handleResponse);
}

function submitUser(userData) {
    var headers = authHeader();
    headers['Content-Type'] = 'application/json';
    const requestOptions = {
        crossDomain: true,
        method: 'POST',
        headers: headers,
        body: JSON.stringify(userData)
    };

    var url = new URL(`${config.apiUrl}/users`);
    return fetch(url, requestOptions).then(handleResponse);
}

function deleteUser(id) {
    const requestOptions = {
        method: 'DELETE',
        headers: authHeader()
    };

    var url = new URL(`${config.apiUrl}/users/` + id);
    return fetch(url, requestOptions).then(handleResponse);
}

function getAddresses(account, query) {
    const requestOptions = {
        method: 'GET',
        headers: authHeader()
    };

    var url = new URL(`${config.apiUrl}/users/addresses`);
    var params = { account: account, query: query };
    Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));
    return fetch(url, requestOptions).then(handleResponse);
}

function getAddress(addressId) {
    const requestOptions = {
        method: 'GET',
        headers: authHeader()
    };

    var url = new URL(`${config.apiUrl}/users/addresses/` + addressId);
    return fetch(url, requestOptions).then(handleResponse);
}

function getJob(jobId) {
    const requestOptions = {
        method: 'GET',
        headers: authHeader()
    };

    var url = new URL(`${config.apiUrl}/jobs/` + jobId);
    return fetch(url, requestOptions).then(handleResponse);
}

function getInvoice(invoiceId) {
    var headers = authHeader();
    headers['Accept'] = 'application/pdf';
    const requestOptions = {
        method: 'GET',
        headers: headers
    };

    var url = new URL(`${config.apiUrl}/invoices/` + invoiceId);
    return fetch(url, requestOptions).then(handleFile);
}

function getInvoices(account, gridDataState) {
    const requestOptions = {
        method: 'GET',
        headers: authHeader()
    };

    var url = new URL(`${config.apiUrl}/invoices`);
    var params = { account: account };
    params["skip"] = gridDataState.skip || 0;
    params["take"] = gridDataState.take || 0;
    if (gridDataState.sort && gridDataState.sort.length) { // UI allows single-sort only
        params["sort_field"] = gridDataState.sort[0].field;
        params["direction"] = gridDataState.sort[0].dir;
    }
    Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));
    return fetch(url, requestOptions).then(handleResponse);
}

function getJobs(job_query, gridDataState) {
    const requestOptions = {
        method: 'GET',
        headers: authHeader()
    };

    var url = new URL(`${config.apiUrl}/jobs`);
    job_query["skip"] = gridDataState.skip || 0;
    job_query["take"] = gridDataState.take || 0;
    job_query["sort"] = gridDataState.sort || [];
    
    if (gridDataState.sort && gridDataState.sort.length) { // UI allows single-sort only
        job_query["sort_field"] = gridDataState.sort[0].field;
        job_query["direction"] = gridDataState.sort[0].dir;
    }
    Object.keys(job_query).forEach(key => url.searchParams.append(key, job_query[key]));
    return fetch(url, requestOptions).then(handleResponse);
}

function handleResponse(response) {
    return response.text().then(text => {
        const data = text && JSON.parse(text);
        if (!response.ok) {
            if (response.status === 401) {
                // auto logout if 401 response returned from api
                logout();
                if(response.url !== `${config.apiUrl}/auth/login` && response.url !== `${config.apiUrl}/users/me`)
                    location.reload(true);

                const errorMessage = (data && data.errors && data.errors[0].message) || (data && data.message) || response.statusText;
                return Promise.reject({
                    status: 401,
                    message: errorMessage
                });
            }
            const error2 = (data && data.errors && data.errors[0].message) || (data && data.message) || response.statusText;
            return Promise.reject(error2);
        }

        return data;
    });
}

function handleFile(response) {
    if (!response.ok) {
        if (response.status === 401) {
            const error = "Not authenticated";
            return Promise.reject(error);
        }
        const error2 = (data && data.errors && data.errors[0].message) || response.statusText;
        return Promise.reject(error2);
    }

    return response.blob()
    .then(blob => {
        const url = window.URL.createObjectURL(new Blob([blob]));
        return url;
    });
}

function recoverPassword(email) {
    const requestOptions = {
        crossDomain: true,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
    };

    return fetch(`${config.apiUrl}/auth/recover`, requestOptions)
        .then(handleResponse);
}

function changePassword(existingPassword, newPassword) {

    var headers = authHeader();
    headers['Content-Type'] = 'application/json';

    const requestOptions = {
        crossDomain: true,
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
            existingPassword: existingPassword,
            newPassword: newPassword
        })
    };

    return fetch(`${config.apiUrl}/users/ChangePassword`, requestOptions)
        .then(handleResponse);
}

function getUserPreferences() {
    const requestOptions = {
        method: 'GET',
        headers: authHeader()
    };

    return fetch(`${config.apiUrl}/users/me/preferences`, requestOptions)
        .then(handleResponse);
}

function submitUserPreferences(defaultPrintLabelFormat) {

    var headers = authHeader();
    headers['Content-Type'] = 'application/json';

    var body = {
        defaultPrintLabelFormat
    };

    if (defaultPrintLabelFormat === '') {
        Object.assign(body, {
            defaultPrintLabelFormat: null
        });
    }

    const requestOptions = {
        crossDomain: true,
        method: 'POST',
        headers: headers,
        body: JSON.stringify(body)
    };

    return fetch(`${config.apiUrl}/users/me/preferences`, requestOptions)
        .then(handleResponse);

}